import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ApiClient } from "@/services/api";
import { PageHeader, LoadingSpinner, Alert, Button, Card } from "@/components/shared";
import { AgentResponseCard } from "@/components/owner/AgentResponseCard";
import { SelectionConfirmModal } from "@/components/owner/SelectionConfirmModal";

const MAX_SELECTIONS = 5;

export default function ResponseReviewPage() {
  const { id: requestId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [responses, setResponses] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchResponses = async () => {
      try {
        const api = new ApiClient();
        const data = await api.get<{ data: any[]; total: number }>(
          `/owner-requests/${requestId}/responses`
        );
        setResponses(data.data);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchResponses();
  }, [requestId]);

  const toggleSelection = (responseId: string) => {
    setSelectedIds((prev) => {
      if (prev.includes(responseId)) return prev.filter((id) => id !== responseId);
      if (prev.length >= MAX_SELECTIONS) return prev;
      return [...prev, responseId];
    });
  };

  const handleConfirmSelections = async () => {
    setSubmitting(true);
    try {
      const api = new ApiClient();
      await api.post("/selections", {
        request_id: requestId,
        response_ids: selectedIds,
      });
      navigate("/owner");
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
      setShowConfirm(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <PageHeader
        title="Agent Responses"
        subtitle={`${responses.length} agent(s) responded • Select up to ${MAX_SELECTIONS} to invite`}
      />

      {error && <Alert type="error" className="mb-6">{error}</Alert>}

      {responses.length === 0 ? (
        <Card>
          <div className="text-center py-8">
            <p className="text-white/50 text-lg">No responses yet</p>
            <p className="text-muted mt-2">
              Agents in your area will review your request and submit proposals.
            </p>
          </div>
        </Card>
      ) : (
        <>
          {selectedIds.length > 0 && (
            <div className="sticky top-0 z-10 bg-midnight border-b border-white/10 py-3 mb-6 -mx-4 px-4">
              <div className="flex items-center justify-between">
                <p className="text-body">
                  <span className="font-bold text-sun">{selectedIds.length}</span> agent(s) selected
                </p>
                <Button variant="primary" onClick={() => setShowConfirm(true)}>
                  Confirm Selection →
                </Button>
              </div>
            </div>
          )}

          <div className="grid gap-6 md:grid-cols-2">
            {responses.map((response) => (
              <AgentResponseCard
                key={response.id}
                response={response}
                onSelect={toggleSelection}
                isSelected={selectedIds.includes(response.id)}
                selectionDisabled={selectedIds.length >= MAX_SELECTIONS}
              />
            ))}
          </div>
        </>
      )}

      {showConfirm && (
        <SelectionConfirmModal
          selectedCount={selectedIds.length}
          maxSelections={MAX_SELECTIONS}
          onConfirm={handleConfirmSelections}
          onCancel={() => setShowConfirm(false)}
          isLoading={submitting}
        />
      )}
    </div>
  );
}
